// src/components/NavNewsletterSignup.jsx
import React, { useState, useContext, useRef } from 'react';
import { AuthContext } from '../context/AuthContext'; // Assuming you still want to use AuthContext for signup
import ReCAPTCHA from 'react-google-recaptcha';
import './NavNewsletterSignup.css'; // We'll create this CSS file next

const RECAPTCHA_SITE_KEY = '6Le1HjcrAAAAAM1ssJZRB9ZSgfhx9UgwkZ3BqERO'; // Use your actual site key

export default function NavNewsletterForm({ onSignupSuccess }) { // Added onSignupSuccess prop
  const { jwt, signup } = useContext(AuthContext); // Only need signup and jwt (to potentially show a "logged in" message)
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    organization: '',
    email: '',
    password: '', // You might want to auto-generate or simplify this if it's just for newsletter
    newsletterConsent: true,
  });
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState(''); // For success feedback
  const [loading, setLoading] = useState(false);
  const recaptchaRef = useRef(null);
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [recaptchaRenderKey, setRecaptchaRenderKey] = useState(Date.now()); // Force re-render if needed

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({
      ...f,
      [name]: type === 'checkbox' ? checked : value,
    }));
    setError(''); // Clear error on change
    setSuccessMessage(''); // Clear success message on change
  };

  const handleRecaptchaChange = (token) => {
    console.log("reCAPTCHA token obtained:", token);
    setRecaptchaToken(token);
    setError('');
  };

  const handleRecaptchaExpire = () => {
    console.log("reCAPTCHA token expired");
    setRecaptchaToken(null);
  };

  const handleRecaptchaError = (err) => {
    console.error("reCAPTCHA error:", err);
    setError("An issue occurred with reCAPTCHA. Please try again.");
    setRecaptchaToken(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setLoading(true);

    if (!recaptchaToken) {
      setError('Please complete the reCAPTCHA verification.');
      setLoading(false);
      return;
    }

    if (!form.newsletterConsent) {
      setError('You must agree to the newsletter terms to sign up.');
      setLoading(false);
      return;
    }

    try {
      // Note: The AuthContext's signup likely handles Mailchimp integration.
      // If not, you'd add Mailchimp API call here or within the signup function.
      const resp = await signup({ // Using the existing signup from AuthContext
        firstName: form.firstName,
        lastName: form.lastName,
        organization: form.organization,
        email: form.email,
        username: form.email, // Assuming username is email
        password: form.password, // Consider if a password is truly needed for just newsletter
                                // If your Strapi requires it for user creation, keep it.
                                // Otherwise, you might auto-generate one or modify backend.
        newsletterConsent: form.newsletterConsent,
        recaptchaToken: recaptchaToken,
      });

      setLoading(false);
      if (resp.error) {
        throw new Error(resp.error.message || 'Sign-up failed. Please try again.');
      }

      setSuccessMessage('Thank you for subscribing!');
      setForm({ // Reset form
        firstName: '',
        lastName: '',
        organization: '',
        email: '',
        password: '',
        newsletterConsent: true,
      });
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
      setRecaptchaToken(null);
      setRecaptchaRenderKey(Date.now()); // Force reCAPTCHA to re-render to clear its state

      if (onSignupSuccess) {
        onSignupSuccess(); // Call the callback prop
      }

    } catch (err) {
      setLoading(false);
      setError(err.message);
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
      setRecaptchaToken(null);
    }
  };

  // If the user is already logged in, maybe show a different message or nothing
  if (jwt) {
    return (
      <div className="nav-newsletter-form-container">
        <p className="nav-newsletter-loggedin-message">You are already subscribed/logged in.</p>
        {/* Optionally, add a logout button if this form is also used in contexts where logout is relevant */}
      </div>
    );
  }

  return (
    <div className="nav-newsletter-form-container" onClick={(e) => e.stopPropagation()}> {/* Prevent event bubbling */}
      {/* You can add the title and summary from NavNewsletterSignup.jsx here if desired */}
      {/* <h2 className="nav-newsletter-form__title">
           Deeper Understanding. Smarter Decisions. Greater Impact.
         </h2>
         <p className="nav-newsletter-form__summary">
           A global community of innovators and strategic thinkers...
         </p> */}
      <form id="nav-newsletter-signup-form" onSubmit={handleSubmit}>
        {/* Using similar field structure to AuthForm for consistency */}
        <div className="form-row-triplet">
          <div className="mc-field-group form-group-inline">
            <label htmlFor="nav-firstName">First Name *</label>
            <input type="text" name="firstName" id="nav-firstName" value={form.firstName} onChange={handleChange} required disabled={loading} />
          </div>
          <div className="mc-field-group form-group-inline">
            <label htmlFor="nav-lastName">Last Name *</label>
            <input type="text" name="lastName" id="nav-lastName" value={form.lastName} onChange={handleChange} required disabled={loading} />
          </div>
          <div className="mc-field-group form-group-inline">
            <label htmlFor="nav-organization">Organization *</label>
            <input type="text" name="organization" id="nav-organization" value={form.organization} onChange={handleChange} required disabled={loading} />
          </div>
        </div>
        <div className="form-row-pair">
          <div className="mc-field-group form-group-inline">
            <label htmlFor="nav-email">Email Address *</label>
            <input type="email" name="email" id="nav-email" value={form.email} onChange={handleChange} required disabled={loading} />
          </div>
          <div className="mc-field-group form-group-inline">
            <label htmlFor="nav-password">Password *</label>
            <input type="password" name="password" id="nav-password" value={form.password} onChange={handleChange} required disabled={loading} placeholder="Create a password"/>
          </div>
        </div>
        <div className="mergeRow gdpr-mergeRow mc-field-group">
          <fieldset className="mc_fieldset gdprRequired mc-field-group">
            <label className="checkbox subfield" htmlFor="nav-newsletterConsent">
              <input type="checkbox" name="newsletterConsent" id="nav-newsletterConsent" checked={form.newsletterConsent} onChange={handleChange} disabled={loading} />
              I agree to sign up to Nexed Insight's Intelligence Network *
            </label>
          </fieldset>
        </div>
        <div className="mc-field-group" style={{ display: 'flex', justifyContent: 'center', margin: '1rem 0' }}>
          <ReCAPTCHA
            key={recaptchaRenderKey}
            ref={recaptchaRef}
            sitekey={RECAPTCHA_SITE_KEY}
            onChange={handleRecaptchaChange}
            onExpired={handleRecaptchaExpire}
            onError={handleRecaptchaError}
          />
        </div>
        <div className="clear">
          <button type="submit" className="button" disabled={loading}>
            {loading ? 'Creating account...' : 'Sign up'}
          </button>
        </div>
        {error && <div className="response response-error" style={{ marginTop: '1rem' }}>{error}</div>}
        {successMessage && <div className="response response-success" style={{ marginTop: '1rem' }}>{successMessage}</div>}
         {/* Add Mailchimp legal text if necessary, similar to NavNewsletterSignup */}
         <div className="nav-newsletter-form__legal-text" style={{ marginTop: '1rem', fontSize: '0.8rem', color: '#555', textAlign: 'center' }}>
            <p>We use Mailchimp as our marketing platform. By clicking Sign up, you acknowledge that your information will be transferred to Mailchimp for processing. <a href="https://mailchimp.com/legal/terms" target="_blank" rel="noopener noreferrer">Learn more</a> about Mailchimp's privacy practices.</p>
            <p>You can unsubscribe at any time by clicking the link in the footer of our emails. For more on how we handle your data, visit our Privacy Policy.</p>
        </div>
      </form>
    </div>
  );
}
